import React from 'react';
import { List, Typography, ConfigProvider, Empty, message } from 'antd';

import logReadableConfig from './logReadableConfig';
import AuditLogListItem from './AuditLogListItem';
import { useGetAuditLogQuery } from './auditLogApi';
import { useCharbeePlanId } from '../../../hooks/usePrerenderUser';
import { ChargeBeePlanId } from '@prerender/billing-shared';
import { AUDIT_LOG_RETENTION_MONTHS } from '@prerender/prerender-shared/FE';
import AuditLogPageLocked from './AuditLogPageLocked';
import UpgradePlanProvider from '../../UpgradePlan/UpgradePlanProvider';

const AUDIT_LOG_HISTORY_DESCRIPTION =`History of every change made by your team members over the last ${AUDIT_LOG_RETENTION_MONTHS} months.`;

function AuditLogPage() {
  const chargebeePlanId = useCharbeePlanId();

  const isLocked = chargebeePlanId === ChargeBeePlanId.Starter;

  return isLocked ? (
    <UpgradePlanProvider>
      <AuditLogPageLocked />
    </UpgradePlanProvider>
  ) : (
    <AuditLogPageUnlocked />
  );
}

function AuditLogPageUnlocked() {
  const { data = [], isFetching, isError } = useGetAuditLogQuery('', { refetchOnMountOrArgChange: true });
  const [messageApi, contextHolder] = message.useMessage();

  const logs = React.useMemo(
    () =>
      data
        .flatMap((log) =>
          Object.entries(log.values).map(([fieldName, fieldValue]) => {
            if (!(fieldName in logReadableConfig)) return null;
            const readableKey = logReadableConfig[fieldName as keyof typeof logReadableConfig];
            if (!readableKey) return null;

            const value = readableKey?.formatter ? readableKey.formatter(fieldValue, log) : fieldValue;
            return {
              ...readableKey,
              modifiedBy: log.modifiedBy,
              modifiedAt: log.modifiedAt,
              value,
            };
          })
        )
        .filter((parsedLog) => parsedLog !== null),
    [data]
  );

  React.useEffect(() => {
    if (isError) {
      messageApi.error('An error occurred while fetching audit logs. Please try again later.');
    }
  }, [isError]);

  return (
    <>
      {contextHolder}
      <Typography.Title level={5}>Audit Log</Typography.Title>
      <Typography.Paragraph type="secondary">{AUDIT_LOG_HISTORY_DESCRIPTION}</Typography.Paragraph>
      <ConfigProvider
        renderEmpty={() => (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="There are no meaningful logs recorded at the moment."
          />
        )}
      >
        <List
          loading={isFetching}
          itemLayout="horizontal"
          dataSource={logs}
          renderItem={(item) => item && <AuditLogListItem item={item} />}
        />
      </ConfigProvider>
    </>
  );
}

export default AuditLogPage;
